import React, { Suspense, useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import ErrorBoundaryV3 from "../../components/provider/ErrorBoundaryV3";
import {
  CurationRuntimeContext,
  type CurationRuntime,
} from "../provider/shared/CurationRuntimeContext";
import {
  createAdminBasketRuntimeService,
  createAdminPortfolioRuntimeService,
  createAdminShareLookupService,
} from "../../services/adminProductsV3Service";
import { productPerformanceService } from "../../services/productPerformanceService";

const BasketCurationPageV3 = React.lazy(
  () => import("../provider/BasketCurationPageV3/BasketCurationPageV3")
);
const PortfolioCurationPageV3 = React.lazy(
  () => import("../provider/PortfolioCurationPageV3/PortfolioCurationPageV3")
);

interface Props {
  mode: "basket" | "portfolio";
}

const ADMIN_PRODUCTS_PATH = "/admin/products";

export default function AdminProductCurationPage({ mode }: Props) {
  const params = useParams<{
    providerId: string;
    basketId: string;
    portfolioId: string;
  }>();

  const providerId = Number(params.providerId);
  const productId =
    mode === "basket" ? Number(params.basketId) : Number(params.portfolioId);

  if (!Number.isFinite(providerId) || providerId <= 0 || !Number.isFinite(productId) || productId <= 0) {
    return <Navigate to={ADMIN_PRODUCTS_PATH} replace />;
  }

  return <ValidatedCurationPage mode={mode} providerId={providerId} productId={productId} />;
}

function ValidatedCurationPage({
  mode,
  providerId,
}: {
  mode: "basket" | "portfolio";
  providerId: number;
  productId: number;
}) {
  const runtime: CurationRuntime = useMemo(() => ({
    scope: "admin" as const,
    providerId,
    basketService: createAdminBasketRuntimeService(providerId),
    portfolioService: createAdminPortfolioRuntimeService(providerId),
    shareService: createAdminShareLookupService(providerId),
    showPerformanceTile: true,
    performanceChartDataSource: "v3-factors-admin",
    getPerformance: (productType, productId) =>
      productPerformanceService.getAdminPerformance(providerId, productType, productId),
    getBackPath: () => ADMIN_PRODUCTS_PATH,
    getBasketEditPath: (bId: number) =>
      `/admin/products/baskets/${providerId}/${bId}`,
    afterPublishSuccess: async ({ navigate }) => {
      navigate(ADMIN_PRODUCTS_PATH);
    },
    afterDeleteSuccess: ({ navigate }) => {
      navigate(ADMIN_PRODUCTS_PATH);
    },
  }), [providerId]);

  return (
    <CurationRuntimeContext.Provider value={runtime}>
      <ErrorBoundaryV3>
        <Suspense fallback={null}>
          {mode === "basket" ? (
            <BasketCurationPageV3 />
          ) : (
            <PortfolioCurationPageV3 />
          )}
        </Suspense>
      </ErrorBoundaryV3>
    </CurationRuntimeContext.Provider>
  );
}
